<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: How did this household's money move in the window, against the window before it?
  Answer:   the income-against-expense chart, and the four headline figures with their change.
  Context:  the expense split by category, the top payees, categories and tags, and the largest transactions.
  Controls: the window, bucket and tab strip in the frame.
-->
<script setup lang="ts">
import {
  absRatio,
  addMoney,
  formatDate,
  formatRatioPercent,
  type Locale,
  MINUS_SIGN,
  moneyShare,
  type Money,
  negateMoney,
  PLUS_SIGN,
  zeroMoney,
} from '@taler/domain'
import { ErrorState } from '@taler/ui'
import { signTone } from '@/app/accounting-view'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { changeBetween, KpiChangeIndicator } from '@/features/dashboard'
import {
  canViewLargestTransactions,
  canViewReporting,
  type ExpenseBreakdownItem,
  CashFlowBarChart,
  ChartLegend,
  chartPeriods,
  type DonutSlice,
  ExpenseDonut,
  groupKeyOf,
  groupLabelOf,
  LargestTransactionsTable,
  pointsVerdict,
  SERIES_COLOURS,
  ratioChange,
  ReportFrame,
  ReportKpiCard,
  TopList,
  type TopListRow,
  useCashFlowQuery,
  useExpenseBreakdownQuery,
  useLargestTransactionsQuery,
  useReportNames,
  useReportScope,
} from '@/features/reporting'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const TOP_ROWS = 5
const DONUT_SLICES = 6

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)
const scope = useReportScope(() => canViewReporting(scope.session.can))
const { session, state, displayCurrency } = scope

const cashFlow = useCashFlowQuery(scope.flows())
const categories = useExpenseBreakdownQuery({ ...scope.flows(), groupBy: () => 'CATEGORY' })
const payees = useExpenseBreakdownQuery({ ...scope.flows(), groupBy: () => 'PAYEE' })
const tags = useExpenseBreakdownQuery({ ...scope.flows(), groupBy: () => 'TAG' })
const largest = useLargestTransactionsQuery({
  ...scope.flows(() => canViewLargestTransactions(session.can)),
  limit: TOP_ROWS,
})
const names = useReportNames()

const windowDays = computed(() => state.range.value.until.epochDay - state.range.value.from.epochDay + 1)
const zero = computed(() => zeroMoney(displayCurrency.value))
const periods = computed(() =>
  chartPeriods(cashFlow.data.value?.periods ?? [], state.range.value, displayCurrency.value),
)

const headline = computed(() => {
  const current = cashFlow.data.value
  if (current === undefined) return undefined
  const base = current.previousPeriod
  return {
    income: changeBetween(base.income, current.income, windowDays.value),
    expense: changeBetween(base.expense, current.expense, windowDays.value),
    net: changeBetween(base.net, current.net, windowDays.value),
    savings: ratioChange(current.savingsRate, base.savingsRate),
    current,
  }
})

const savingsDelta = computed(() => {
  const change = headline.value?.savings
  if (change === undefined) return undefined
  const sign = change.direction < 0 ? MINUS_SIGN : change.direction > 0 ? PLUS_SIGN : ''
  return {
    text: `${sign}${formatRatioPercent(absRatio(change.points), { locale: currentLocale.value, minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`,
    verdict: pointsVerdict(change),
  }
})

const VERDICT_CLASSES = {
  good: 'bg-[color:color-mix(in_srgb,var(--color-positive)_12%,transparent)] text-[color:var(--color-positive)]',
  bad: 'bg-[color:color-mix(in_srgb,var(--color-negative)_12%,transparent)] text-[color:var(--color-negative)]',
  flat: 'bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted)]',
} as const

const donut = computed<DonutSlice[]>(() => {
  const items = categories.data.value?.items ?? []
  const total = categories.data.value?.total ?? zero.value
  const slices: DonutSlice[] = items.slice(0, DONUT_SLICES).map((item, index) => ({
    key: groupKeyOf(item.group),
    label: groupLabel(item),
    amount: item.amount,
    share: item.share,
    colour: SERIES_COLOURS[index]!,
  }))
  const rest = items.slice(DONUT_SLICES)
  if (rest.length > 0) {
    const other: Money = rest.reduce((sum, item) => addMoney(sum, item.amount), zero.value)
    slices.push({
      key: 'other',
      label: t('reporting.other'),
      amount: other,
      share: moneyShare(other, total),
      colour: SERIES_COLOURS[DONUT_SLICES]!,
    })
  }
  return slices
})

function groupLabel(item: ExpenseBreakdownItem): string {
  return groupLabelOf(item.group, {
    accountLabel: (accountId) => names.labelOf(accountId),
    noPayee: t('reporting.noPayee'),
    untagged: t('reporting.untagged'),
    unknownAccount: t('reporting.unknownAccount'),
  })
}

function topRows(items: readonly ExpenseBreakdownItem[] | undefined, withIcon: boolean): TopListRow[] {
  return (items ?? []).slice(0, TOP_ROWS).map((item) => ({
    key: groupKeyOf(item.group),
    label: groupLabel(item),
    icon: withIcon && item.group.kind === 'category' ? names.iconOf(item.group.accountId) : undefined,
    amount: item.amount,
    share: item.share,
  }))
}

const topPayees = computed(() => topRows(payees.data.value?.items, false))
const topCategories = computed(() => topRows(categories.data.value?.items, true))
const topTags = computed(() => topRows(tags.data.value?.items, false))

const largestRows = computed(() =>
  (largest.data.value ?? []).map((row) => ({
    key: row.entryId,
    entryId: row.entryId,
    date: formatDate(row.date, currentLocale.value, 'short'),
    payee: row.payee ?? t('reporting.noPayee'),
    category: names.labelOf(row.categoryAccountId) ?? t('reporting.severalCategories'),
    categoryIcon: names.iconOf(row.categoryAccountId),
    account: names.labelOf(row.accountId) ?? t('reporting.severalAccounts'),
    amount: row.amount,
    tags: row.tags,
  })),
)

const VS_PREVIOUS = computed(() => t('reporting.kpi.vsPrevious'))

const flowLegend = computed(() => [
  { key: 'income', label: t('reporting.chart.income'), colour: 'var(--color-income)' },
  { key: 'expense', label: t('reporting.chart.expenses'), colour: 'var(--color-expense)' },
])
</script>

<template>
  <ReportFrame :title="t('reporting.title')" :subtitle="t('reporting.overview.subtitle')" :state="state">
    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <p v-else-if="!canViewReporting(session.can)" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noAccess') }}
    </p>

    <template v-else>
      <ErrorState
        v-if="cashFlow.error.value"
        :title="t('reporting.errorTitle')"
        :retry-label="t('reporting.retry')"
        :retrying="cashFlow.retrying.value"
        @retry="cashFlow.retry()"
      />
      <section
        v-else-if="headline"
        class="grid grid-cols-1 gap-[var(--space-group)] md:grid-cols-2 xl:grid-cols-4"
      >
        <ReportKpiCard :label="t('reporting.kpi.income')" icon="income">
          <MoneyAmount :value="headline.current.income" sign tone="income" size="large" />
          <template #change>
            <KpiChangeIndicator :change="headline.income" favourable="up" :caption="VS_PREVIOUS" />
          </template>
        </ReportKpiCard>
        <ReportKpiCard :label="t('reporting.kpi.expenses')" icon="expense">
          <MoneyAmount :value="negateMoney(headline.current.expense)" sign tone="expense" size="large" />
          <template #change>
            <KpiChangeIndicator :change="headline.expense" favourable="down" :caption="VS_PREVIOUS" />
          </template>
        </ReportKpiCard>
        <ReportKpiCard :label="t('reporting.kpi.net')" icon="net">
          <MoneyAmount
            :value="headline.current.net"
            sign
            :tone="signTone(headline.current.net)"
            size="large"
          />
          <template #change>
            <KpiChangeIndicator :change="headline.net" favourable="up" :caption="VS_PREVIOUS" />
          </template>
        </ReportKpiCard>
        <ReportKpiCard :label="t('reporting.kpi.savings')" icon="savings">
          <span
            class="text-[length:var(--text-step-5)] font-semibold tabular-nums text-[color:var(--color-text)]"
          >
            {{
              headline.current.savingsRate === undefined
                ? '—'
                : `${formatRatioPercent(headline.current.savingsRate, { locale: currentLocale, minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`
            }}
          </span>
          <template #change>
            <p
              v-if="savingsDelta"
              class="flex flex-wrap items-center gap-[var(--space-inline)] text-[length:var(--text-step-1)]"
            >
              <span
                class="inline-flex items-center rounded-full px-[var(--space-inline)] py-px font-semibold tabular-nums"
                :class="VERDICT_CLASSES[savingsDelta.verdict]"
              >
                {{ savingsDelta.text }}
              </span>
              <span class="text-[color:var(--color-text-muted)]">{{ VS_PREVIOUS }}</span>
            </p>
          </template>
        </ReportKpiCard>
      </section>

      <section
        class="grid grid-cols-1 gap-[var(--space-group)] xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]"
      >
        <figure
          class="flex min-w-0 flex-col gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-group)]"
        >
          <figcaption class="flex flex-wrap items-center justify-between gap-[var(--space-inline)]">
            <span class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
              {{ t('reporting.chart.incomeVsExpenses') }}
            </span>
            <ChartLegend :items="flowLegend" />
          </figcaption>
          <CashFlowBarChart
            :periods="periods"
            :currency="displayCurrency"
            :bucket="state.range.value.bucket"
            mode="flows"
            :legend="false"
          />
        </figure>
        <section
          class="flex min-w-0 flex-col gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-group)]"
        >
          <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
            {{ t('reporting.expensesByCategory') }}
          </h2>
          <ExpenseDonut
            v-if="categories.data.value"
            :slices="donut"
            :total="categories.data.value.total"
            :caption="t('reporting.expensesByCategory')"
          />
        </section>
      </section>

      <section class="grid grid-cols-1 gap-[var(--space-group)] lg:grid-cols-3">
        <TopList
          :title="t('reporting.topPayees')"
          :rows="topPayees"
          :view-all="{ name: 'reporting-payees' }"
        />
        <TopList
          :title="t('reporting.topCategories')"
          :rows="topCategories"
          :view-all="{ name: 'reporting-categories' }"
        />
        <TopList :title="t('reporting.topTags')" :rows="topTags" :view-all="{ name: 'reporting-tags' }" />
      </section>

      <LargestTransactionsTable
        v-if="canViewLargestTransactions(session.can)"
        :rows="largestRows"
        :title="t('reporting.largest.title')"
      />
    </template>
  </ReportFrame>
</template>
